/**
 * HATCH boundary-path parsing — extracted from entityParser.ts to keep the
 * monolith under control (CLAUDE.md "Контроль размера файлов").
 *
 * A HATCH (AcDbHatch) boundary section runs from group `91` (path count) up to
 * group `75` (hatch style). Per path, group `92` is a path-type bitfield:
 *   1  = external
 *   2  = POLYLINE   ← bit 1 selects polyline vs edge form; they parse DIFFERENTLY
 *   4  = derived
 *   8  = textbox
 *   16 = outermost
 *
 * Two boundary-path forms exist (BOTH supported here):
 *
 *  A) POLYLINE  (`92 & 2`) — what most real AutoCAD hatches + the showcase use:
 *       72 hasBulge   73 isClosed   93 vertexCount   then 10/20 (+42 bulge) per
 *       vertex   97 sourceObjectCount (path ends).
 *     The model has no polyline edge kind, so consecutive vertices v[i]→v[i+1]
 *     become a LINE edge (bulge==0) or an ARC edge (bulge≠0; DXF bulge =
 *     tan(θ/4)). When isClosed, the closing edge v[last]→v[0] is appended.
 *
 *  B) EDGE-BASED (`92` without bit 2) — curved boundaries + the writer's output:
 *       93 edgeCount   then per edge a 72 type selector:
 *         72 1 → line          : 10/20 start, 11/21 end
 *         72 2 → arc           : 10/20 center, 40 radius, 50/51 angles(deg), 73 ccw
 *         72 3 → ellipticArc   : 10/20 center, 11/21 majorAxisEnd, 40 minorRatio,
 *                                50/51 angles(deg), 73 ccw
 *         72 4 → spline        : control structure — tessellated to line edges
 *       97 sourceObjectCount (path ends).
 *
 * Angles (50/51) are DEGREES in DXF and stay degrees in the model — the writer
 * emits them verbatim (`pushGroup(50, edge.startAngle)`) and hatchConverter
 * multiplies by DEG_TO_RAD. Source of truth: DXF reference (HATCH group codes)
 * + ezdxf `ezdxf.entities.boundary_paths` (PolylinePath / EdgePath).
 *
 * State-machine collision gating: codes 10/20/11/21/40/50/51/72/73/93/42 are
 * reused by the pattern definition (after 75) and seed points (after 98). The
 * caller flips `inHatchBoundary` true on 91, false on 75, and `inHatchSeed`
 * true on 98; every reader here no-ops (delegates to passthrough) when
 * `inHatchBoundary` is false, leaving the existing pattern / seed handling
 * intact.
 */

import {
  type DxfHatchBoundaryEdge,
  type DxfHatchBoundaryPath,
  type DxfPoint3D,
  point3D,
} from "./types";

/** Path-type bit 1 (value 2) — selects the POLYLINE boundary form. */
const PATH_TYPE_POLYLINE_BIT = 2;

/**
 * In-flight polyline-path accumulator. Vertices are collected as the parser
 * walks 10/20 (+42 bulge) pairs; converted to edges when the path closes.
 */
export type HatchPolylineState = {
  hasBulge: boolean;
  isClosed: boolean;
  vertices: { x: number; y: number; bulge: number }[];
  /** In-flight vertex — 10 sets x, 20 sets y, 42 sets bulge. */
  current: { x?: number; y?: number; bulge?: number } | null;
};

/**
 * In-flight edge cursor for the EDGE-BASED form. A `72` selector opens a new
 * partial edge of the chosen kind; subsequent geometry codes fill it; the
 * partial is flushed (validated → typed edge) when the next `72`, `97`, the
 * next `92`, or the closing `75` arrives.
 */
export type HatchEdgeState = {
  kind: "line" | "arc" | "ellipticArc" | "spline";
  // line
  start?: Partial<DxfPoint3D>;
  end?: Partial<DxfPoint3D>;
  // arc / ellipticArc
  center?: Partial<DxfPoint3D>;
  radius?: number;
  // ellipticArc
  majorAxisEndpoint?: Partial<DxfPoint3D>;
  minorAxisRatio?: number;
  // arc / ellipticArc shared
  startAngle?: number;
  endAngle?: number;
  counterclockwise?: boolean;
  // spline (72=4) — control structure captured for tessellation
  spline?: {
    degree?: number;
    rational?: boolean;
    periodic?: boolean;
    knots: number[];
    controlPoints: { x?: number; y?: number; weight?: number }[];
    fitPoints: { x?: number; y?: number }[];
    currentControl: { x?: number; y?: number; weight?: number } | null;
    currentFit: { x?: number; y?: number } | null;
    /**
     * The spline structure ends with a `97` = fit-point count, followed by
     * `11`/`21` fit-point pairs, and ONLY THEN the path's own terminating `97`.
     * Once this spline's internal fit-count `97` has been consumed we flip this
     * so a later `97` (the real terminator) finalizes the path instead of being
     * mistaken for another fit-count (FIX 5). `fitRemaining` counts the 11/21
     * fit points still to consume after the count was read.
     */
    fitCountSeen: boolean;
    fitRemaining: number;
  };
};

/** Mutable cursor for the current boundary path (polyline OR edge form). */
export type HatchCurrentPath = {
  pathType: number;
  isPolyline: boolean;
  edges: DxfHatchBoundaryEdge[];
  polyline: HatchPolylineState | null;
  currentEdge: HatchEdgeState | null;
  /**
   * FIX A — the declared edge count (group 93) for an EDGE-BASED path. Lets a
   * trailing lone elevation `30` be recognised even when the writer omits the
   * terminator `97`: the path geometry is complete once `edges.length` plus a
   * complete-but-unflushed `currentEdge` reaches this count. `undefined` for a
   * polyline path or until a `93` is read.
   */
  edgesExpected: number | undefined;
  /**
   * S-HATCH-ASSOC-REFS — associative source-boundary object handles (group
   * 330) accumulated for this path. Normally populated AFTER the path's `97`
   * terminator (which has already finalized + cleared this cursor), so the
   * common case attaches via the finalized `hatchBoundaryPaths` entry; this
   * field handles the rare no-`97` form where the cursor is still open. Carried
   * onto the finalized `DxfHatchBoundaryPath.sourceBoundaryHandles`.
   */
  sourceBoundaryHandles?: string[];
};

/**
 * The slice of the entity draft this module reads/writes. `SupportedEntityDraft`
 * in entityParser.ts includes these exact fields so it is assignable here
 * without a circular import of the full draft type.
 */
export type HatchBoundaryDraft = {
  hatchBoundaryPaths: DxfHatchBoundaryPath[];
  hatchCurrentPath: HatchCurrentPath | null;
  inHatchBoundary: boolean;
  inHatchSeed: boolean;
  /**
   * Declared boundary-path count from group `91`. The drawing-model export
   * omits group `75` for the default `hatchStyle: 0`, so the boundary section
   * can end with NO `75` to close it. We close the section once this many real
   * paths have been finalized (FIX 1) so a subsequent pattern-scale `41` is no
   * longer swallowed as in-boundary geometry. `undefined` until a `91` is read.
   */
  hatchBoundaryPathTarget: number | undefined;
  /** Count of real boundary paths pushed onto `hatchBoundaryPaths` (FIX 1). */
  hatchBoundaryFinalizedCount: number;
  /**
   * FIX A — true once the declared path count (group 91) has been finalized,
   * INCLUDING when the last path closes via an explicit terminator `97`.
   * `inHatchBoundary` stays true at that moment (so trailing associative `330`
   * source handles are still consumed — FIX 3), but the boundary GEOMETRY is
   * complete. This distinguishes a per-edge boundary Z `30` (paths NOT yet
   * complete → consume + drop) from the HATCH ELEVATION `30` the writer emits
   * after the last path when 75/76/41 are all omitted (paths complete → close
   * the boundary + route the `30` to the elevation handler instead of dropping
   * it). `undefined` semantics: false until the count is met.
   */
  hatchBoundaryPathsComplete: boolean;
  /**
   * FIX A — true immediately after a boundary point's Y (group 20/21) is read,
   * meaning a following `30`/`31` is THAT point's Z (per-point boundary Z, drop
   * it). Reset by every other boundary reader so a LONE `30`/`31` (one not
   * directly following a 20/21) is recognised as the HATCH elevation rather than
   * a boundary point Z — even when the writer omits the terminator `97`. This is
   * what lets the writer's terminatorless output round-trip its elevation.
   */
  hatchBoundaryAwaitingZ: boolean;
};

function num(code: number, value: string): number {
  const parsed = Number(value.trim());
  if (!Number.isFinite(parsed)) {
    throw new Error(`Group ${code} expected a finite number, received "${value}"`);
  }
  return parsed;
}

function int(code: number, value: string): number {
  const parsed = Number.parseInt(value.trim(), 10);
  if (!Number.isInteger(parsed)) {
    throw new Error(`Group ${code} expected an integer, received "${value}"`);
  }
  return parsed;
}

function toPoint(p: Partial<DxfPoint3D> | undefined): DxfPoint3D {
  return point3D(p?.x ?? 0, p?.y ?? 0, p?.z ?? 0);
}

/* ── bulge → arc ───────────────────────────────────────────────────────────
 * DXF bulge = tan(θ/4) where θ is the included (signed) arc angle from start to
 * end. bulge>0 ⇒ counter-clockwise (left) turn, bulge<0 ⇒ clockwise. Given the
 * two endpoints (p0, p1) and the bulge we recover the arc centre, radius and
 * the start/end angles AutoCAD would store. Mirrors ezdxf
 * `ezdxf.math.bulge_to_arc`.
 */
export function bulgeToArc(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  bulge: number,
): DxfHatchBoundaryEdge {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const chord = Math.hypot(dx, dy);

  // Degenerate chord (coincident endpoints) — fall back to a line edge so the
  // path still closes rather than producing a NaN-radius arc.
  if (chord === 0) {
    return { edgeType: "line", start: point3D(p0.x, p0.y, 0), end: point3D(p1.x, p1.y, 0) };
  }

  // Included angle θ = 4·atan(bulge). Radius from the chord/half-angle relation:
  //   sin(θ/2) = (chord/2) / radius  ⇒  radius = (chord/2) / sin(θ/2).
  const theta = 4 * Math.atan(bulge);
  const halfTheta = theta / 2;
  const sinHalf = Math.sin(halfTheta);
  // Near-straight segment (|bulge|→0) — sin(θ/2)≈0 ⇒ radius→∞; treat as a line.
  if (Math.abs(sinHalf) < 1e-12) {
    return { edgeType: "line", start: point3D(p0.x, p0.y, 0), end: point3D(p1.x, p1.y, 0) };
  }
  const radius = Math.abs(chord / 2 / sinHalf);

  // Centre lies on the perpendicular bisector of the chord, offset by the
  // sagitta-derived apothem. distanceToCenter = radius·cos(θ/2). The sign of
  // the offset direction follows the bulge sign (ccw = left of the chord).
  const midX = (p0.x + p1.x) / 2;
  const midY = (p0.y + p1.y) / 2;
  // Unit chord direction and its left normal.
  const ux = dx / chord;
  const uy = dy / chord;
  // Left normal of the chord direction.
  const nx = -uy;
  const ny = ux;
  // apothem = radius·cos(θ/2); bulge>0 (ccw) ⇒ centre is to the LEFT of the
  // chord direction (positive normal); bulge<0 ⇒ right (negative normal).
  const apothem = radius * Math.cos(halfTheta);
  const sign = bulge > 0 ? 1 : -1;
  const cx = midX + nx * apothem * sign;
  const cy = midY + ny * apothem * sign;

  const startAngle = Math.atan2(p0.y - cy, p0.x - cx);
  const endAngle = Math.atan2(p1.y - cy, p1.x - cx);
  const RAD_TO_DEG = 180 / Math.PI;
  // The model/DXF stores angles measured CCW from +X; the renderer sweeps
  // start→end in the direction given by `counterclockwise`. bulge>0 ⇒ ccw.
  return {
    edgeType: "arc",
    center: point3D(cx, cy, 0),
    radius,
    startAngle: startAngle * RAD_TO_DEG,
    endAngle: endAngle * RAD_TO_DEG,
    counterclockwise: bulge > 0,
  };
}

/* ── spline tessellation (72=4) ────────────────────────────────────────────
 * Spline boundary edges are rare (the writer never emits them) but a real DXF
 * may carry one. Rather than risk corrupting the stream by skipping, we
 * tessellate the spline into a short polyline of LINE edges so the loop still
 * closes and renders. We sample the control polygon when no usable curve can be
 * built — a defensive, never-crash approximation (full NURBS evaluation is a
 * separate, larger slice).
 */
function tessellateSplineEdge(state: HatchEdgeState): DxfHatchBoundaryEdge[] {
  const sp = state.spline;
  if (!sp || sp.controlPoints.length < 2) return [];
  const pts = sp.controlPoints.map((c) => ({ x: c.x ?? 0, y: c.y ?? 0 }));
  const edges: DxfHatchBoundaryEdge[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    edges.push({
      edgeType: "line",
      start: point3D(pts[i]!.x, pts[i]!.y, 0),
      end: point3D(pts[i + 1]!.x, pts[i + 1]!.y, 0),
    });
  }
  return edges;
}

/**
 * Flush the in-flight edge cursor (EDGE-BASED form) into the path's edge list.
 * Validates that the required geometry was read; an incomplete edge is dropped
 * so a malformed stream cannot mint a NaN edge.
 */
function flushCurrentEdge(path: HatchCurrentPath): void {
  const e = path.currentEdge;
  if (!e) return;
  path.currentEdge = null;

  if (e.kind === "line") {
    if (e.start === undefined || e.end === undefined) return;
    path.edges.push({ edgeType: "line", start: toPoint(e.start), end: toPoint(e.end) });
    return;
  }
  if (e.kind === "arc") {
    if (e.center === undefined || e.radius === undefined) return;
    path.edges.push({
      edgeType: "arc",
      center: toPoint(e.center),
      radius: e.radius,
      startAngle: e.startAngle ?? 0,
      endAngle: e.endAngle ?? 360,
      counterclockwise: e.counterclockwise ?? true,
    });
    return;
  }
  if (e.kind === "ellipticArc") {
    if (
      e.center === undefined ||
      e.majorAxisEndpoint === undefined ||
      e.minorAxisRatio === undefined
    ) {
      return;
    }
    path.edges.push({
      edgeType: "ellipticArc",
      center: toPoint(e.center),
      majorAxisEndpoint: toPoint(e.majorAxisEndpoint),
      minorAxisRatio: e.minorAxisRatio,
      startAngle: e.startAngle ?? 0,
      endAngle: e.endAngle ?? 360,
      counterclockwise: e.counterclockwise ?? true,
    });
    return;
  }
  if (e.kind === "spline") {
    for (const edge of tessellateSplineEdge(e)) {
      path.edges.push(edge);
    }
  }
}

/**
 * FIX A — is the in-flight EDGE-BASED edge structurally complete (all required
 * geometry read)? Mirrors `flushCurrentEdge`'s validation so a complete-but-
 * unflushed `currentEdge` counts toward path-geometry completeness. A spline is
 * complete once it has ≥2 control points (it tessellates to line edges).
 */
function isCurrentEdgeComplete(e: HatchEdgeState | null): boolean {
  if (!e) return false;
  if (e.kind === "line") return e.start !== undefined && e.end !== undefined;
  if (e.kind === "arc") return e.center !== undefined && e.radius !== undefined;
  if (e.kind === "ellipticArc") {
    return (
      e.center !== undefined &&
      e.majorAxisEndpoint !== undefined &&
      e.minorAxisRatio !== undefined
    );
  }
  // spline
  return (e.spline?.controlPoints.length ?? 0) >= 2;
}

/**
 * FIX A — has the current EDGE-BASED path read all its declared (group 93)
 * edges? Counts already-flushed edges plus a complete-but-unflushed current
 * edge. Returns false when the count is unknown (no 93) or the path is a
 * polyline (polyline completeness is governed by 73/the next 92, not a lone 30).
 */
function isEdgePathGeometryComplete(path: HatchCurrentPath): boolean {
  if (path.isPolyline) return false;
  if (path.edgesExpected === undefined) return false;
  const complete = path.edges.length + (isCurrentEdgeComplete(path.currentEdge) ? 1 : 0);
  return complete >= path.edgesExpected;
}

/**
 * FIX A — would a lone elevation `30`/`31` arriving NOW mean every declared
 * boundary path (group 91) is geometrically complete? True when all but the
 * current path are finalized AND the current edge-based path has read all its
 * declared edges. This recognises the writer's TERMINATORLESS output (no `97`,
 * no `75`/`76`/`41`), where the elevation is the only post-boundary code, while
 * a per-point boundary Z (which immediately follows its 10/20 inside a still-
 * incomplete edge set) is NOT mistaken for the elevation.
 */
function loneZIsElevation(draft: HatchBoundaryDraft): boolean {
  if (draft.hatchBoundaryPathsComplete) return true;
  const target = draft.hatchBoundaryPathTarget;
  if (target === undefined) return false;
  const path = draft.hatchCurrentPath;
  if (!path) {
    // No open path — every finalized path counts; complete iff the target is met.
    return draft.hatchBoundaryFinalizedCount >= target;
  }
  if (!isEdgePathGeometryComplete(path)) return false;
  return draft.hatchBoundaryFinalizedCount + 1 >= target;
}

/** Flush an in-flight polyline vertex into the vertex list. */
function flushPolylineVertex(poly: HatchPolylineState): void {
  const v = poly.current;
  if (!v) return;
  poly.current = null;
  poly.vertices.push({ x: v.x ?? 0, y: v.y ?? 0, bulge: v.bulge ?? 0 });
}

/** Convert an accumulated polyline path into model edges (line/arc). */
function polylineToEdges(poly: HatchPolylineState): DxfHatchBoundaryEdge[] {
  flushPolylineVertex(poly);
  const v = poly.vertices;
  const edges: DxfHatchBoundaryEdge[] = [];
  const segCount = poly.isClosed ? v.length : v.length - 1;
  for (let i = 0; i < segCount; i++) {
    const a = v[i]!;
    const b = v[(i + 1) % v.length]!;
    // The bulge attached to vertex i describes the segment i → i+1.
    if (poly.hasBulge && a.bulge !== 0) {
      edges.push(bulgeToArc(a, b, a.bulge));
    } else {
      edges.push({
        edgeType: "line",
        start: point3D(a.x, a.y, 0),
        end: point3D(b.x, b.y, 0),
      });
    }
  }
  return edges;
}

/**
 * Finalize the current boundary path and push it onto `hatchBoundaryPaths`.
 * Idempotent: a no-op when there is no current path (e.g. an empty boundary).
 */
export function finalizeHatchCurrentPath(
  draft: HatchBoundaryDraft,
  fromTerminator = false,
): void {
  const path = draft.hatchCurrentPath;
  if (!path) return;
  draft.hatchCurrentPath = null;

  // S-HATCH-ASSOC-REFS — carry any source handles already accumulated on the
  // cursor (the no-`97` form). The common `97`-terminated form attaches them to
  // this finalized entry afterwards via readHatchBoundarySourceHandle.
  const sourceHandles =
    path.sourceBoundaryHandles && path.sourceBoundaryHandles.length > 0
      ? { sourceBoundaryHandles: path.sourceBoundaryHandles }
      : {};
  if (path.isPolyline && path.polyline) {
    const edges = polylineToEdges(path.polyline);
    draft.hatchBoundaryPaths.push({ pathType: path.pathType, edges, ...sourceHandles });
  } else {
    // Edge-based: flush any trailing in-flight edge first.
    flushCurrentEdge(path);
    draft.hatchBoundaryPaths.push({ pathType: path.pathType, edges: path.edges, ...sourceHandles });
  }

  draft.hatchBoundaryFinalizedCount += 1;

  // FIX A — mark the boundary GEOMETRY complete once the declared path count
  // (group 91) is finalized, REGARDLESS of how the last path closed (count,
  // 75/76/41, OR an explicit terminator `97`). This is the gate that lets a
  // trailing lone `30` be recognised as the HATCH elevation rather than a
  // boundary point Z (read30 below). We keep this independent of the
  // inHatchBoundary flip so the terminator-97 path (which keeps inHatchBoundary
  // true for trailing `330` consumption — FIX 3) still records completion.
  const targetMet =
    draft.hatchBoundaryPathTarget !== undefined &&
    draft.hatchBoundaryFinalizedCount >= draft.hatchBoundaryPathTarget;
  if (targetMet) {
    draft.hatchBoundaryPathsComplete = true;
  }

  // FIX 1 — close the boundary section once the declared path count (group 91)
  // is consumed, even when the closing group 75 is OMITTED (the drawing-model
  // export drops 75 for the default hatchStyle 0). Without this, inHatchBoundary
  // stays true and the following pattern-scale 41 is swallowed as boundary
  // passthrough, silently resetting patternScale on round-trip.
  //
  // We DO NOT count-close when finalizing through an explicit path terminator
  // (`97`): an AutoCAD stream that emits `97` also emits the closing `75` (and,
  // for associative paths, trailing `330` source handles that must still be
  // consumed in-boundary — FIX 3). The 75/76 readers close those streams; the
  // count-close is reserved for the 97-less, 75-less writer output. When the
  // terminator-97 path completes the count, we deliberately KEEP inHatchBoundary
  // true (so trailing `330` stays consumed) but rely on hatchBoundaryPathsComplete
  // to disambiguate a following lone `30` elevation (FIX A).
  if (!fromTerminator && targetMet) {
    draft.inHatchBoundary = false;
  }
}

/** Open a new boundary path from a `92` path-type flag. */
function openPath(draft: HatchBoundaryDraft, pathType: number): void {
  // A previous path without an explicit 97 terminator (e.g. the writer's
  // output, which omits 97) closes when the next 92 arrives.
  finalizeHatchCurrentPath(draft);
  const isPolyline = (pathType & PATH_TYPE_POLYLINE_BIT) !== 0;
  draft.hatchCurrentPath = {
    pathType,
    isPolyline,
    edges: [],
    polyline: isPolyline
      ? { hasBulge: false, isClosed: false, vertices: [], current: null }
      : null,
    currentEdge: null,
    edgesExpected: undefined,
  };
}

/** Begin a new edge cursor for the EDGE-BASED form (`72` selector). */
function openEdge(path: HatchCurrentPath, edgeTypeCode: number): void {
  flushCurrentEdge(path);
  switch (edgeTypeCode) {
    case 1:
      path.currentEdge = { kind: "line" };
      break;
    case 2:
      path.currentEdge = { kind: "arc" };
      break;
    case 3:
      path.currentEdge = { kind: "ellipticArc" };
      break;
    case 4:
      path.currentEdge = {
        kind: "spline",
        spline: {
          knots: [],
          controlPoints: [],
          fitPoints: [],
          currentControl: null,
          currentFit: null,
          fitCountSeen: false,
          fitRemaining: 0,
        },
      };
      break;
    default:
      // Unknown edge selector — ignore so the stream is not corrupted.
      path.currentEdge = null;
  }
}

function ensurePoint(target: Partial<DxfPoint3D> | undefined): Partial<DxfPoint3D> {
  return target ?? {};
}

/**
 * Boundary group-code readers. Each is gated on `draft.inHatchBoundary`; when
 * false the reader returns `false` and the caller routes the group to the
 * existing pattern / seed / passthrough handling. When the reader consumed the
 * group it returns `true`.
 */
export type HatchBoundaryReader = (draft: HatchBoundaryDraft, value: string) => boolean;

/** Reads a 10 (x) — vertex (polyline) or start/centre x (edge). */
const read10: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const path = draft.hatchCurrentPath;
  if (!path) return true; // inside boundary but no open path yet — swallow.
  if (path.isPolyline && path.polyline) {
    const poly = path.polyline;
    flushPolylineVertex(poly);
    poly.current = { x: num(10, value) };
    return true;
  }
  const e = path.currentEdge;
  if (!e) return true;
  if (e.kind === "line") {
    e.start = ensurePoint(e.start);
    e.start.x = num(10, value);
  } else if (e.kind === "arc" || e.kind === "ellipticArc") {
    e.center = ensurePoint(e.center);
    e.center.x = num(10, value);
  } else if (e.kind === "spline" && e.spline) {
    e.spline.currentControl = e.spline.currentControl ?? {};
    e.spline.currentControl.x = num(10, value);
  }
  return true;
};

/** Reads a 20 (y) — vertex (polyline) or start/centre y (edge). */
const read20: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  // A boundary point's Y was just read — a directly-following 30 is THAT point's
  // Z, not the elevation (FIX A).
  draft.hatchBoundaryAwaitingZ = true;
  const path = draft.hatchCurrentPath;
  if (!path) return true;
  if (path.isPolyline && path.polyline) {
    const cur = path.polyline.current;
    if (cur) cur.y = num(20, value);
    return true;
  }
  const e = path.currentEdge;
  if (!e) return true;
  if (e.kind === "line") {
    e.start = ensurePoint(e.start);
    e.start.y = num(20, value);
  } else if (e.kind === "arc" || e.kind === "ellipticArc") {
    e.center = ensurePoint(e.center);
    e.center.y = num(20, value);
  } else if (e.kind === "spline" && e.spline) {
    if (e.spline.currentControl) {
      e.spline.currentControl.y = num(20, value);
      e.spline.controlPoints.push(e.spline.currentControl);
      e.spline.currentControl = null;
    }
  }
  return true;
};

/** Reads an 11 (x) — line end x / ellipticArc major-axis endpoint x / spline fit-point x. */
const read11: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (!e) return true;
  if (e.kind === "line") {
    e.end = ensurePoint(e.end);
    e.end.x = num(11, value);
  } else if (e.kind === "ellipticArc") {
    e.majorAxisEndpoint = ensurePoint(e.majorAxisEndpoint);
    e.majorAxisEndpoint.x = num(11, value);
  } else if (e.kind === "spline" && e.spline) {
    // Spline FIT point x (after the fit-count 97). We tessellate from CONTROL
    // points, so fit points are consumed-and-ignored — they must not leak to
    // extraGroups, and must not be confused with anything else (FIX 5).
    e.spline.currentFit = e.spline.currentFit ?? {};
    e.spline.currentFit.x = num(11, value);
  }
  return true;
};

/** Reads a 21 (y) — line end y / ellipticArc major-axis endpoint y / spline fit-point y. */
const read21: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  // A boundary point's Y was just read — a directly-following 31 is THAT point's
  // Z, not the elevation (FIX A).
  draft.hatchBoundaryAwaitingZ = true;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (!e) return true;
  if (e.kind === "line") {
    e.end = ensurePoint(e.end);
    e.end.y = num(21, value);
  } else if (e.kind === "ellipticArc") {
    e.majorAxisEndpoint = ensurePoint(e.majorAxisEndpoint);
    e.majorAxisEndpoint.y = num(21, value);
  } else if (e.kind === "spline" && e.spline) {
    if (e.spline.currentFit) {
      e.spline.currentFit.y = num(21, value);
      e.spline.fitPoints.push(e.spline.currentFit);
      e.spline.currentFit = null;
      if (e.spline.fitRemaining > 0) e.spline.fitRemaining -= 1;
    }
  }
  return true;
};

/** Reads a 40 — arc radius / ellipticArc minor-axis ratio / spline knot. */
const read40: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (!e) return true;
  if (e.kind === "arc") {
    e.radius = num(40, value);
  } else if (e.kind === "ellipticArc") {
    e.minorAxisRatio = num(40, value);
  } else if (e.kind === "spline" && e.spline) {
    e.spline.knots.push(num(40, value));
  }
  return true;
};

/** Reads a 50 — arc/ellipticArc start angle (degrees). */
const read50: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (!e) return true;
  if (e.kind === "arc" || e.kind === "ellipticArc") {
    e.startAngle = num(50, value);
  }
  return true;
};

/** Reads a 51 — arc/ellipticArc end angle (degrees). */
const read51: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (!e) return true;
  if (e.kind === "arc" || e.kind === "ellipticArc") {
    e.endAngle = num(51, value);
  }
  return true;
};

/**
 * Reads a 30 — boundary point Z. The model boundary points are 2D (z defaults
 * to 0 and the writer always emits z=0), so the boundary Z is consumed and
 * DROPPED while in-boundary. Crucially, this prevents the per-edge-point 30/31
 * the writer emits from falling to `extraGroups` and accumulating stray 30/31
 * outside the boundary section on import/export cycles (FIX 2). When NOT
 * in-boundary the reader returns false so the HATCH elevation 30 (emitted AFTER
 * the boundary) keeps its existing handling.
 */
const read30: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const directlyAfterPointY = draft.hatchBoundaryAwaitingZ;
  draft.hatchBoundaryAwaitingZ = false;
  // FIX A — a LONE `30` (one NOT directly following a boundary point's Y) inside
  // the boundary is NOT a per-point Z — it is the HATCH ELEVATION the writer
  // emits after the last path when 75/76/41 are all omitted (default-style
  // export, non-zero elevation). This holds both when the last path closed via a
  // terminator 97 (hatchBoundaryPathsComplete) AND the writer's TERMINATORLESS
  // output (path geometry complete via the declared edge count). Close the
  // boundary and return false so the group routes to the existing (non-boundary)
  // elevation handling and is preserved, not dropped.
  if (!directlyAfterPointY && loneZIsElevation(draft)) {
    draft.inHatchBoundary = false;
    return false;
  }
  // Validate (throws on a non-finite Z) but discard — boundary points are 2D.
  num(30, value);
  return true;
};

/** Reads a 31 — boundary line-end / major-axis-endpoint Z (consumed, see read30). */
const read31: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const directlyAfterPointY = draft.hatchBoundaryAwaitingZ;
  draft.hatchBoundaryAwaitingZ = false;
  // A lone 31 (not directly following a 21) inside the boundary mirrors read30's
  // elevation case (DXF stores elevation as group 30, but guard symmetrically).
  if (!directlyAfterPointY && loneZIsElevation(draft)) {
    draft.inHatchBoundary = false;
    return false;
  }
  num(31, value);
  return true;
};

/** Reads a 42 — polyline-vertex bulge (only in polyline form). */
const read42: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const poly = draft.hatchCurrentPath?.polyline;
  if (poly?.current) poly.current.bulge = num(42, value);
  return true;
};

/**
 * FIX C — reads a spline-edge TANGENT-vector component. A HATCH spline boundary
 * edge (`72 4`) may carry an optional start tangent (`12`/`22`) and end tangent
 * (`13`/`23`) AFTER the control points / knots. We tessellate the spline from its
 * CONTROL polygon, so the tangents are informational — but they MUST be consumed
 * within the spline edge so they do not leak to `extraGroups` and re-emit as
 * stray `12`/`22`/`13`/`23` OUTSIDE the boundary on import/export cycles.
 *
 * Gated on BOTH `inHatchBoundary` AND the current edge being a spline. Outside
 * the boundary, or for a non-spline edge, the reader returns false so any
 * legitimate use of `12`/`13` elsewhere (none in HATCH — they are spline-only
 * here) is unaffected and the group passes through unchanged. We validate the
 * value (throws on a non-finite number) but discard it.
 */
const readSplineTangent = (code: number): HatchBoundaryReader => {
  return (draft, value) => {
    if (!draft.inHatchBoundary) return false;
    const e = draft.hatchCurrentPath?.currentEdge;
    if (!e || e.kind !== "spline") return false;
    num(code, value);
    return true;
  };
};

/**
 * Reads a 72. In POLYLINE form this is the has-bulge flag. In EDGE-BASED form
 * it is the edge-type selector (1=line/2=arc/3=ellipticArc/4=spline).
 */
const read72: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const path = draft.hatchCurrentPath;
  if (!path) return true;
  if (path.isPolyline && path.polyline) {
    path.polyline.hasBulge = int(72, value) !== 0;
  } else {
    openEdge(path, int(72, value));
  }
  return true;
};

/**
 * Reads a 73. In POLYLINE form this is the is-closed flag. In EDGE-BASED form
 * it is the arc/ellipticArc counter-clockwise flag.
 */
const read73: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const path = draft.hatchCurrentPath;
  // Group 73 is BOUNDARY-ONLY (the pattern double-line flag is group 77). While
  // in-boundary it is the POLYLINE is-closed flag or the EDGE-BASED arc /
  // elliptic-arc CCW flag (spline rational flag — informational). Consume it as
  // a boundary flag so it never leaks to extraGroups; with no current edge it is
  // still a boundary code (consumed, no effect) — it can NEVER set doubleLine.
  if (path?.isPolyline && path.polyline) {
    path.polyline.isClosed = int(73, value) !== 0;
    return true;
  }
  const e = path?.currentEdge;
  if (e && (e.kind === "arc" || e.kind === "ellipticArc")) {
    e.counterclockwise = int(73, value) !== 0;
    return true;
  }
  // Spline rational flag, a LINE edge's stray 73, or a 73 with no open path:
  // still a boundary-section group while in-boundary — consume it so it does not
  // fall through to passthrough / extraGroups.
  return true;
};

/**
 * Reads a 93. POLYLINE: vertex count (informational — we accumulate actual
 * 10/20 pairs). EDGE-BASED: edge count (informational). No-op beyond marking
 * consumption; kept so the count code does not fall through to passthrough.
 */
const read93: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  // Record the declared edge count for an EDGE-BASED path so a trailing lone
  // elevation 30 can be recognised without a terminator 97 (FIX A). Harmless for
  // a polyline path (it carries 93 = vertex count; we accumulate actual 10/20
  // pairs and never consult edgesExpected for polylines).
  const path = draft.hatchCurrentPath;
  if (path && !path.isPolyline) {
    const count = int(93, value);
    path.edgesExpected = Number.isFinite(count) && count >= 0 ? count : undefined;
  }
  return true;
};

/**
 * Reads a spline-edge structural count/flag (94=degree, 95=knot count,
 * 96=control-point count, 74=periodic). We tessellate from the control polygon
 * so these are informational, but they MUST be consumed while in-boundary so
 * the spline structure does not leak to `extraGroups` (FIX 5 — only meaningful
 * for a spline edge; harmless no-op otherwise since these codes only appear in a
 * spline edge's structure).
 */
const readSplineStructural: HatchBoundaryReader = (draft) => {
  // Counts/flags are implied by the accumulated knot (40) / control-point
  // (10/20) arrays we tessellate from, so we only need to CONSUME the group
  // while in-boundary (returning true) so it does not leak to extraGroups.
  return draft.inHatchBoundary;
};

/**
 * Reads a 97. Two distinct meanings inside the boundary section (FIX 5):
 *
 *   1. SPLINE FIT-POINT COUNT — for an in-progress spline edge (`72 4`) whose
 *      control structure has been read, the FIRST `97` is the number of fit
 *      points, followed by `11`/`21` fit pairs. It must NOT close the path.
 *   2. PATH TERMINATOR — the source-object count that ends the path. Any `97`
 *      that is NOT a spline's still-unseen fit-count finalizes the path.
 *
 * We tessellate splines from CONTROL points, so the fit block is consumed and
 * ignored — but it must be consumed within the edge so the spline's internal
 * `97` + `11`/`21` never truncate the path or leak to `extraGroups`.
 */
const read97: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  // 97 is never a boundary point's Z — clear the awaiting-Z flag so a LATER lone
  // 30 (the elevation the writer emits after a terminator 97 when 75/76/41 are
  // omitted, possibly past trailing 330 source handles) is recognised as the
  // elevation rather than the last point's Z and dropped (FIX A).
  draft.hatchBoundaryAwaitingZ = false;
  const e = draft.hatchCurrentPath?.currentEdge;
  if (e && e.kind === "spline" && e.spline && !e.spline.fitCountSeen) {
    // This 97 is the spline's fit-point count, not the path terminator.
    e.spline.fitCountSeen = true;
    const count = int(97, value);
    e.spline.fitRemaining = Number.isFinite(count) && count > 0 ? count : 0;
    return true;
  }
  finalizeHatchCurrentPath(draft, /* fromTerminator */ true);
  return true;
};

/**
 * Map of boundary-only readers keyed by group code. The caller consults this
 * map FIRST (while inHatchBoundary may be true); a reader that returns `false`
 * (because the boundary section is closed) lets the caller fall back to the
 * existing HATCH reader / common reader / passthrough.
 */
export const HATCH_BOUNDARY_READERS: Record<number, HatchBoundaryReader> = {
  10: read10,
  20: read20,
  30: read30,
  11: read11,
  21: read21,
  31: read31,
  // FIX C — spline-edge start tangent (12/22) + end tangent (13/23). Consumed
  // only while in-boundary AND the current edge is a spline; passthrough otherwise.
  12: readSplineTangent(12),
  22: readSplineTangent(22),
  13: readSplineTangent(13),
  23: readSplineTangent(23),
  40: read40,
  50: read50,
  51: read51,
  42: read42,
  72: read72,
  73: read73,
  74: readSplineStructural,
  93: read93,
  94: readSplineStructural,
  95: readSplineStructural,
  96: readSplineStructural,
  97: read97,
};

/**
 * Reads a 330 inside the boundary section (FIX 3). For an associative path with
 * `97 > 0`, the following `330` groups are SOURCE-boundary object handles, NOT
 * the entity owner. While `inHatchBoundary` we CONSUME them so they never reach
 * the common owner-handle reader and overwrite the HATCH's real `ownerHandle`
 * (which was read in the AcDbEntity section, before group 91). When NOT
 * in-boundary the reader returns false so the entity owner 330 keeps its
 * existing handling.
 *
 * S-HATCH-ASSOC-REFS — instead of dropping the consumed handle we attach it to
 * the boundary path it belongs to so an associative hatch's source references
 * round-trip (handle-preservation only; live re-association is out of scope).
 * The `97` terminator finalizes the path (clearing `hatchCurrentPath`) BEFORE
 * its trailing `330`s arrive, so the handles attach to the LAST finalized path
 * (`hatchBoundaryPaths[length-1]`). When the path closed via a count/marker
 * with NO explicit `97` (`hatchCurrentPath` may still be open), fall back to
 * the current path's accumulator.
 */
export const readHatchBoundarySourceHandle: HatchBoundaryReader = (draft, value) => {
  if (!draft.inHatchBoundary) return false;
  const handle = value.trim();
  const open = draft.hatchCurrentPath;
  if (open) {
    (open.sourceBoundaryHandles ??= []).push(handle);
    return true;
  }
  const last = draft.hatchBoundaryPaths[draft.hatchBoundaryPaths.length - 1];
  if (last) {
    (last.sourceBoundaryHandles ??= []).push(handle);
  }
  return true;
};

/**
 * Handle the `92` path-type group. Always consumed while parsing a HATCH (a
 * `92` only appears inside the boundary section). Opens a new path.
 */
export function readHatchPath92(draft: HatchBoundaryDraft, value: string): void {
  openPath(draft, int(92, value));
}
